import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../context/User';
import { StatusContext } from '../context/Status';

import { Button, Container, Dropdown, Icon, Menu, Segment } from 'semantic-ui-react';
import { API, getLogo, getSystemName, isAdmin, isMobile, showSuccess } from '../helpers';
import '../index.css';

// 动态构建 Header Buttons（根据 Git 功能是否启用）
const getHeaderButtons = (gitEnabled) => {
  const systemManagementSubItems = [
    {
      name: '用户列表',
      to: '/user',
      icon: 'user'
    }
  ];

  // 仅在 Git 功能启用时添加 Git 仓库菜单
  if (gitEnabled) {
    systemManagementSubItems.push({
      name: 'Git仓库',
      to: '/git',
      icon: 'code branch'
    });
  }

  systemManagementSubItems.push(
    {
      name: '邀请码',
      to: '/invite_code',
      icon: 'gift'
    },
    {
      name: 'Action 管理',
      to: '/action',
      icon: 'lightning'
    },
    {
      name: '数据收集',
      to: '/data-collection',
      icon: 'archive'
    },
    {
      name: '数据分析',
      to: '/analytics',
      icon: 'chart area'
    },
    {
      name: '日志',
      to: '/logs',
      icon: 'book'
    },
    {
      name: '数据管理',
      to: '/data-management',
      icon: 'database'
    },
    {
      name: '反馈管理',
      to: '/feedback',
      icon: 'comments'
    },
    {
      name: '脚本案例库',
      to: '/script-examples',
      icon: 'code'
    },
    {
      name: '执行计费',
      to: '/execution-billing',
      icon: 'dollar sign'
    },
    {
      name: '动态Prompt',
      to: '/dynamic-prompts',
      icon: 'cogs'
    },
    {
      name: '动态API',
      to: '/dynamic-apis',
      icon: 'code'
    }
  );

  return [
    {
      name: '首页',
      to: '/',
      icon: 'home'
    },
    {
      name: '令牌',
      to: '/token',
      icon: 'key'
    },
    {
      name: '兑换',
      to: '/redemption',
      icon: 'dollar sign',
      admin: true
    },
    {
      name: '充值',
      to: '/topup',
      icon: 'cart'
    },
    {
      name: '渠道',
      to: '/channel',
      icon: 'sitemap',
      admin: true
    },
    {
      name: '系统管理',
      icon: 'server',
      admin: true,
      subItems: systemManagementSubItems
    },
    {
      name: '脚本案例库',
      to: '/examples',
      icon: 'book'
    },
    {
      name: '问题反馈',
      to: '/feedback/submit',
      icon: 'comment'
    },
    {
      name: 'Token使用',
      to: '/token-usage',
      icon: 'chart line'
    },
    {
      name: '设置',
      to: '/setting',
      icon: 'setting'
    },
    {
      name: '关于',
      to: '/about',
      icon: 'info circle'
    }
  ];
};

const Header = () => {
  const [userState, userDispatch] = useContext(UserContext);
  const [statusState] = useContext(StatusContext);
  let navigate = useNavigate();

  const [showSidebar, setShowSidebar] = useState(false);
  const systemName = getSystemName();
  const logo = getLogo();

  // 根据系统状态动态构建菜单（包含 Git 功能判断）
  const gitEnabled = statusState?.status?.git_enabled !== false;
  const headerButtons = React.useMemo(() => {
    let buttons = getHeaderButtons(gitEnabled);
    // 动态添加聊天链接
    if (localStorage.getItem('chat_link')) {
      buttons.splice(1, 0, {
        name: '聊天',
        to: '/chat',
        icon: 'comments'
      });
    }
    return buttons;
  }, [gitEnabled]);

  async function logout() {
    setShowSidebar(false);
    await API.get('/api/user/logout');
    showSuccess('注销成功!');
    userDispatch({ type: 'logout' });
    localStorage.removeItem('user');
    navigate('/login');
  }

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const renderButtons = (isMobile) => {
    return headerButtons.map((button, index) => {
      if (button.admin && !isAdmin()) return <React.Fragment key={index}></React.Fragment>;
      
      // 如果有子菜单项
      if (button.subItems) {
        if (isMobile) {
          // 移动端显示子菜单项
          return (
            <React.Fragment key={button.name}>
              <Menu.Item header>
                <Icon name={button.icon} />
                {button.name}
              </Menu.Item>
              {button.subItems.map((subItem) => (
                <Menu.Item
                  key={subItem.name}
                  onClick={() => {
                    navigate(subItem.to);
                    setShowSidebar(false);
                  }}
                  style={{ paddingLeft: '2em' }}
                >
                  <Icon name={subItem.icon} />
                  {subItem.name}
                </Menu.Item>
              ))}
            </React.Fragment>
          );
        } else {
          // 桌面端使用下拉菜单
          return (
            <Dropdown key={button.name} item text={button.name} icon={button.icon}>
              <Dropdown.Menu>
                {button.subItems.map((subItem) => (
                  <Dropdown.Item
                    key={subItem.name}
                    as={Link}
                    to={subItem.to}
                  >
                    <Icon name={subItem.icon} />
                    {subItem.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          );
        }
      }
      
      // 普通菜单项
      if (isMobile) {
        return (
          <Menu.Item
            key={button.name}
            onClick={() => {
              navigate(button.to);
              setShowSidebar(false);
            }}
          >
            <Icon name={button.icon} />
            {button.name}
          </Menu.Item>
        );
      }
      return (
        <Menu.Item key={button.name} as={Link} to={button.to}>
          <Icon name={button.icon} />
          {button.name}
        </Menu.Item>
      );
    });
  };

  if (isMobile()) {
    return (
      <>
        <Menu
          borderless
          size='large'
          style={
            showSidebar
              ? {
                borderBottom: 'none',
                marginBottom: '0',
                borderTop: 'none',
                height: '51px'
              }
              : { borderTop: 'none', height: '52px' }
          }
        >
          <Container>
            <Menu.Item as={Link} to='/'>
              <img
                src={logo}
                alt='logo'
                style={{ marginRight: '0.75em' }}
              />
              <div style={{ fontSize: '20px' }}>
                <b>{systemName}</b>
              </div>
            </Menu.Item>
            <Menu.Menu position='right'>
              <Menu.Item onClick={toggleSidebar}>
                <Icon name={showSidebar ? 'close' : 'sidebar'} />
              </Menu.Item>
            </Menu.Menu>
          </Container>
        </Menu>
        {showSidebar ? (
          <Segment style={{ marginTop: 0, borderTop: '0' }}>
            <Menu secondary vertical style={{ width: '100%', margin: 0 }}>
              {renderButtons(true)}
              <Menu.Item>
                {userState.user ? (
                  <Button onClick={logout}>注销</Button>
                ) : (
                  <>
                    <Button
                      onClick={() => {
                        setShowSidebar(false);
                        navigate('/login');
                      }}
                    >
                      登录
                    </Button>
                    <Button
                      onClick={() => {
                        setShowSidebar(false);
                        navigate('/register');
                      }}
                    >
                      注册
                    </Button>
                  </>
                )}
              </Menu.Item>
            </Menu>
          </Segment>
        ) : (
          <></>
        )}
      </>
    );
  }

  return (
    <>
      <Menu borderless style={{ borderTop: 'none' }}>
        <Container>
          <Menu.Item as={Link} to='/' className={'hide-on-mobile'}>
            <img src={logo} alt='logo' style={{ marginRight: '0.75em' }} />
            <div style={{ fontSize: '20px' }}>
              <b>{systemName}</b>
            </div>
          </Menu.Item>
          {renderButtons(false)}
          <Menu.Menu position='right'>
            {userState.user ? (
              <Dropdown
                text={userState.user.username}
                pointing
                className='link item'
              >
                <Dropdown.Menu>
                  <Dropdown.Item onClick={logout}>注销</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <Menu.Item
                name='登录'
                as={Link}
                to='/login'
                className='btn btn-link'
              />
            )}
          </Menu.Menu>
        </Container>
      </Menu>
    </>
  );
};

export default Header;
